import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { APP_STORE_URL } from '../constants/mobileLinks';

const BANNER_DISMISSED_STORAGE_KEY = 'ariza-ios-app-banner-dismissed';
const SKIP_PATH_PREFIXES = ['/admin', '/a', '/y'];

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  const isiOSUA = /iphone|ipad|ipod/.test(ua);
  const isIPadOSDesktopUA = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isiOSUA || isIPadOSDesktopUA;
}

export function IOSAppBanner() {
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  React.useEffect(() => {
    if (!isIOSDevice() || SKIP_PATH_PREFIXES.some((path) => window.location.pathname.startsWith(path))) {
      return;
    }

    try {
      if (window.localStorage.getItem(BANNER_DISMISSED_STORAGE_KEY) !== 'true') {
        setIsBannerVisible(true);
      }
    } catch {
      // If storage is unavailable, still show the banner for this session.
      setIsBannerVisible(true);
    }

    setIsModalVisible(true);
  }, []);

  const dismissBanner = React.useCallback(() => {
    setIsBannerVisible(false);
    try {
      window.localStorage.setItem(BANNER_DISMISSED_STORAGE_KEY, 'true');
    } catch {
      // Ignore storage failures; closing should still work.
    }
  }, []);

  const dismissModal = React.useCallback(() => {
    setIsModalVisible(false);
  }, []);

  if (!isBannerVisible && !isModalVisible) {
    return null;
  }

  const overlay =
    typeof document !== 'undefined'
      ? createPortal(
          <>
            {isBannerVisible && (
              <div
                className="fixed inset-x-0 top-0 z-50 border-b border-sky-200 bg-white px-3 py-2 shadow-sm"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(135deg, rgba(14,165,233,0.12) 0, rgba(14,165,233,0.12) 1px, transparent 1px, transparent 10px)',
                }}
              >
                <div className="mx-auto flex w-full max-w-7xl items-center gap-3">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-sky-100 bg-white shadow-sm">
                    <img src="/Arıza.webp" alt="Arıza Rehberim" className="h-full w-full object-contain" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-glass-900">Arıza Rehberim</p>
                    <p className="truncate text-xs text-glass-700">iPhone uygulamasını indir</p>
                  </div>

                  <a
                    href={APP_STORE_URL}
                    className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-700"
                    aria-label="Arıza Rehberim uygulamasını App Store'dan indir"
                  >
                    <span className="text-sm leading-none" aria-hidden="true">
                      
                    </span>
                    İndir
                  </a>

                  <button
                    type="button"
                    onClick={dismissBanner}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/75 text-glass-500 transition hover:bg-white hover:text-glass-800"
                    aria-label="iOS uygulama bannerını kapat"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {isModalVisible && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
                <div
                  className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-sky-100 bg-white p-5 shadow-2xl"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ios-app-modal-title"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(135deg, rgba(14,165,233,0.1) 0, rgba(14,165,233,0.1) 1px, transparent 1px, transparent 12px)',
                  }}
                >
                  <button
                    type="button"
                    onClick={dismissModal}
                    className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-glass-500 transition hover:bg-white hover:text-glass-900"
                    aria-label="Uygulama indirme popupını kapat"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  <div className="flex items-center gap-3 pr-10">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-sky-100 bg-white shadow-sm">
                      <img src="/Arıza.webp" alt="Arıza Rehberim" className="h-full w-full object-contain" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">iPhone uygulaması</p>
                      <h2 id="ios-app-modal-title" className="text-lg font-bold leading-tight text-glass-900">
                        Arıza Rehberim App Store'da
                      </h2>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-glass-700">
                    Daha hızlı kullanım için Arıza Rehberim'i iPhone'una indir.
                  </p>

                  <a
                    href={APP_STORE_URL}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700"
                    aria-label="Arıza Rehberim uygulamasını App Store'dan indir"
                  >
                    <span className="text-lg leading-none" aria-hidden="true">
                      
                    </span>
                    App Store'dan İndir
                  </a>

                  <button
                    type="button"
                    onClick={dismissModal}
                    className="mt-3 w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-glass-600 transition hover:bg-white/70 hover:text-glass-900"
                  >
                    Web'de devam et
                  </button>
                </div>
              </div>
            )}
          </>,
          document.body
        )
      : null;

  return (
    <>
      {isBannerVisible && <div className="h-[57px]" aria-hidden="true" />}
      {overlay}
    </>
  );
}
